import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi, getAdminToken, type AdminFirm, type AdminUser } from '../../lib/admin-api';

const ROLE_STYLE: Record<NonNullable<AdminUser['role']>, string> = {
  owner: 'bg-gold/20 text-ink border-gold',
  accountant: 'bg-blue-100 text-blue-900 border-blue-300',
  assistant: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  viewer: 'bg-zinc-100 text-zinc-700 border-zinc-300',
};

export function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [firms, setFirms] = useState<AdminFirm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [resetFor, setResetFor] = useState<AdminUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    password: '',
    firm_id: '',
    role: 'owner' as NonNullable<AdminUser['role']>,
  });
  const [resetPw, setResetPw] = useState('');

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setError(null);
    try {
      const [u, f] = await Promise.all([adminApi.listUsers(), adminApi.listFirms()]);
      setUsers(u.users);
      setFirms(f.firms);
      if (f.firms.length > 0 && !form.firm_id) {
        setForm((prev) => ({ ...prev, firm_id: f.firms[0]!.id }));
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await adminApi.createUser(form);
      setShowCreate(false);
      setForm({ email: '', full_name: '', password: '', firm_id: form.firm_id, role: 'owner' });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPw(e: React.FormEvent) {
    e.preventDefault();
    if (!resetFor) return;
    setSaving(true);
    setError(null);
    try {
      await adminApi.resetPassword(resetFor.id, resetPw);
      setResetFor(null);
      setResetPw('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/settings" className="text-taupe text-sm hover:text-ink">← Settings</Link>
          <h2 className="font-serif text-3xl font-bold mt-2">Utilisateurs</h2>
          <p className="text-taupe text-sm">
            Tous les comptes utilisateurs avec leur cabinet de rattachement et rôle. Mots de passe hashés PBKDF2-SHA256.
          </p>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} disabled={firms.length === 0} className="btn-primary disabled:opacity-50" title={firms.length === 0 ? 'Crée d\'abord un cabinet' : ''}>
          + Créer un utilisateur
        </button>
      </div>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}
      {users === null && !error && <div className="text-taupe text-sm">Chargement…</div>}
      {users !== null && users.length === 0 && (
        <div className="card text-center py-12 text-taupe">Aucun utilisateur. Crée un cabinet, puis un utilisateur.</div>
      )}

      {users !== null && users.length > 0 && (
        <div className="card !p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream/40 text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Nom</th>
                <th className="px-4 py-2 text-left">Cabinet</th>
                <th className="px-4 py-2 text-left">Rôle</th>
                <th className="px-4 py-2 text-left">Créé</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border/50 hover:bg-cream/30">
                  <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2">{u.full_name}</td>
                  <td className="px-4 py-2 text-xs">{u.firm_name ?? <span className="text-taupe italic">—</span>}</td>
                  <td className="px-4 py-2">
                    {u.role && <span className={`text-xs px-2 py-0.5 rounded border ${ROLE_STYLE[u.role]}`}>{u.role}</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-taupe">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setResetFor(u)}
                      className="text-xs px-2 py-1 rounded border border-border hover:border-gold"
                    >
                      Réinit. mot de passe
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-ink/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleCreate} className="bg-cream rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wider text-taupe">Nouvel utilisateur</div>
              <h3 className="font-serif text-xl font-bold">Création</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <label className="block">
                <span className="font-medium">Email</span>
                <input
                  required type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-border rounded px-3 py-2 mt-1"
                />
              </label>
              <label className="block">
                <span className="font-medium">Nom complet</span>
                <input
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full border border-border rounded px-3 py-2 mt-1"
                />
              </label>
              <label className="block">
                <span className="font-medium">Mot de passe initial (≥ 8 car.)</span>
                <input
                  required type="password" minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-border rounded px-3 py-2 mt-1 font-mono"
                />
              </label>
              <label className="block">
                <span className="font-medium">Cabinet</span>
                <select
                  required
                  value={form.firm_id}
                  onChange={(e) => setForm({ ...form, firm_id: e.target.value })}
                  className="w-full border border-border rounded px-3 py-2 mt-1"
                >
                  {firms.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.id.slice(-8)})
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="font-medium">Rôle</span>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as NonNullable<AdminUser['role']> })}
                  className="w-full border border-border rounded px-3 py-2 mt-1"
                >
                  <option value="owner">Owner — accès total</option>
                  <option value="accountant">Comptable — opérations métier</option>
                  <option value="assistant">Assistant — saisie</option>
                  <option value="viewer">Lecteur — consultation</option>
                </select>
              </label>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Annuler</button>
              <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Création…' : 'Créer'}</button>
            </div>
          </form>
        </div>
      )}

      {resetFor && (
        <div className="fixed inset-0 bg-ink/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleResetPw} className="bg-cream rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wider text-taupe">Réinitialisation du mot de passe</div>
              <h3 className="font-serif text-xl font-bold">{resetFor.full_name}</h3>
              <div className="font-mono text-xs text-taupe">{resetFor.email}</div>
            </div>
            <div className="p-5 space-y-2 text-sm">
              <label className="block">
                <span className="font-medium">Nouveau mot de passe (≥ 8 car.)</span>
                <input
                  required type="password" minLength={8} autoFocus
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  className="w-full border border-border rounded px-3 py-2 mt-1 font-mono"
                />
              </label>
              <p className="text-xs text-taupe">L'utilisateur sera obligé de se reconnecter avec le nouveau mot de passe.</p>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button type="button" onClick={() => { setResetFor(null); setResetPw(''); }} className="btn-secondary">Annuler</button>
              <button type="submit" disabled={saving || resetPw.length < 8} className="btn-primary">
                {saving ? 'Mise à jour…' : 'Mettre à jour'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return iso;
  }
}
